import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { PageHead, Progress } from "../../components/ui-bits";
import { useAgentClient } from "../../components/AgentContext";
import { Buildings, Briefcase, CheckCircle, FileText, Ticket, Warning, ArrowRight, Hourglass } from "@phosphor-icons/react";

export default function AgentDashboard() {
  const { clients, setSel, loaded } = useAgentClient();
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [comp, setComp] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/agent/tasks").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/agent/tickets").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/compliance").then((r) => setComp(r.data)).catch(() => {});
  }, []);

  const pendDocs = (ts) => ts.reduce((a, t) => a + (t.required_docs || []).filter((r) => r.status !== "received").length, 0);
  const active = tasks.filter((t) => t.status !== "Completed");
  const kpis = [
    { label: "Clients", value: clients.length, Icon: Buildings },
    { label: "Active Jobs", value: active.length, Icon: Briefcase },
    { label: "Completed", value: tasks.length - active.length, Icon: CheckCircle },
    { label: "Docs Awaited", value: pendDocs(tasks), Icon: FileText, warn: pendDocs(tasks) > 0 },
    { label: "Awaiting Approval", value: tasks.filter((t) => t.approval_status === "pending").length, Icon: Hourglass },
    { label: "Open Admin Tasks", value: tickets.filter((t) => t.status !== "Resolved").length, Icon: Ticket, warn: tickets.some((t) => t.status === "Open") },
    { label: "Cash at Risk", value: clients.filter((c) => c.cash_alert_value != null).length, Icon: Warning, warn: clients.some((c) => c.cash_alert_value != null) },
  ];
  const deadlines = comp.filter((c) => c.status !== "completed").sort((a, b) => (a.date > b.date ? 1 : -1)).slice(0, 6);
  const company = (email) => clients.find((c) => c.email === email)?.company || email;
  const open = (email, path) => { setSel(email); navigate(path); };

  return (
    <div>
      <PageHead eyebrow="Your Clients at a Glance" title="Dashboard" />
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
        {kpis.map((k, i) => (
          <div key={k.label} data-testid={`agent-kpi-${i}`} className="card p-5">
            <div className="flex items-center justify-between mb-2"><p className="label">{k.label}</p><k.Icon size={18} className={k.warn ? "text-[#FF3B30]" : "text-[#002FA7]"} /></div>
            <p className={`font-head font-black text-3xl tnum ${k.warn ? "text-[#FF3B30]" : ""}`}>{k.value}</p>
          </div>
        ))}
      </div>

      <p className="label mb-3">My Clients</p>
      {clients.length === 0 ? <p className="card p-8 text-sm text-[#888] text-center mb-6">{loaded ? "No clients assigned yet." : "Loading clients…"}</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {clients.map((c, i) => {
            const ct = tasks.filter((t) => t.client_email === c.email);
            const avg = ct.length ? Math.round(ct.reduce((a, t) => a + (t.progress || 0), 0) / ct.length) : 0;
            const pd = pendDocs(ct);
            return (
              <button key={c.email} data-testid={`agent-client-card-${i}`} onClick={() => open(c.email, "/agent/jobs")}
                className={`card p-5 text-left relative hover:-translate-y-0.5 hover:border-[#002FA7] transition-[transform,border-color] ${c.cash_alert_value != null ? "border-[#DC2626]/50" : ""}`}>
                {c.cash_alert_value != null && (
                  <span data-testid={`agent-cash-risk-${i}`} role="button" title="Open client's bookkeeping" onClick={(e) => { e.stopPropagation(); open(c.email, "/agent/bookkeeping"); }}
                    className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider bg-[#DC2626] text-white px-2 py-1 rounded-sm inline-flex items-center gap-1">
                    <Warning size={11} weight="fill" /> Cash at risk · {inr(c.cash_alert_value)}
                  </span>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center"><Buildings size={20} className="text-[#002FA7]" weight="fill" /></div>
                  <div className={`min-w-0 ${c.cash_alert_value != null ? "pt-5" : ""}`}><p className="font-head font-bold truncate">{c.company}</p><p className="text-xs text-[#888] truncate">{c.plan || "—"} · {(c.services || []).join(", ") || "No services"}</p></div>
                </div>
                <div className="flex items-center gap-2 mb-3"><Progress value={avg} /><span className="tnum text-xs font-semibold w-9 text-right">{avg}%</span></div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#555]">{ct.filter((t) => t.status !== "Completed").length} active / {ct.length} jobs</span>
                  {pd > 0 ? <span className="text-[#8a6d00]">{pd} doc(s) awaited</span> : <span className="text-[#00A86B]">Docs complete</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card p-6 min-w-0">
          <p className="label mb-4 flex items-center gap-2"><Warning size={16} color="#FF3B30" /> Upcoming Client Deadlines</p>
          <div className="space-y-3">
            {deadlines.map((d, i) => (
              <div key={d.id || i} data-testid={`agent-deadline-${i}`} className="flex justify-between items-center text-sm gap-3">
                <span className="truncate">{d.name} <span className="text-xs text-[#888]">· {company(d.client_email)}</span></span>
                <span className={`tnum shrink-0 ${d.status === "overdue" ? "text-[#FF3B30] font-semibold" : "text-[#555]"}`}>{d.date}</span>
              </div>
            ))}
            {deadlines.length === 0 && <p className="text-sm text-[#888]">No pending deadlines.</p>}
          </div>
        </div>
        <div className="card p-6 min-w-0">
          <p className="label mb-4 flex items-center gap-2"><Briefcase size={16} color="#002FA7" /> Jobs Needing Attention</p>
          <div className="space-y-3">
            {active.slice(0, 6).map((t, i) => (
              <button key={t.id} data-testid={`agent-attn-${i}`} onClick={() => open(t.client_email, "/agent/jobs")} className="w-full flex items-center gap-3 text-sm text-left hover:text-[#002FA7]">
                <span className="flex-1 truncate">{t.service} <span className="text-xs text-[#888]">· {company(t.client_email)}</span></span>
                <span className="tnum text-xs">{t.progress}%</span><ArrowRight size={12} />
              </button>
            ))}
            {active.length === 0 && <p className="text-sm text-[#888]">All jobs are up to date.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
